import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Image, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

import {Markup} from '@amblique/base/app/components/shared/markup';
import {PIM_SQUARE_THUMBNAIL_PX, resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {
  formatCurrencyFullPrecision,
  formatCurrencyHideTrailingZeroCents,
} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';

/**
 * Individual configuration product tile (radio-style).
 * Displays thumbnail (first image from c_pimlyConfigurationImage array), title, short description,
 * price, and a radio indicator. On mobile, "View details" opens the full gallery panel.
 */
const SelectorTile = ({hit, isSelected, onSelect, onViewDetails}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const {currency} = useCurrency();

  const title = hit.displayTitle ?? hit.name ?? '';
  const shortDescription = hit.shortDescription;
  const imageUrl = resolveProductThumbnailUrl(hit);
  const badgeText = hit.c_badgeText ?? hit.badgeText ?? '';

  // Price: two parts (min / max) from c_priceProcessed (SFRA) or normalized
  const prices = hit.prices ?? {};
  const salePrice = prices.salePrice ?? hit.price;
  const listPrice = prices.listPrice ?? salePrice;
  const priceMin = prices.min ?? hit.priceMin ?? salePrice;
  const priceMax = prices.max ?? hit.priceMax ?? salePrice;
  const hasDiscount = listPrice != null && salePrice != null && listPrice > salePrice;
  const isRange = hit.priceType === 'range' && priceMin != null && priceMax != null && priceMin !== priceMax;

  // For range: use listPrice (retail pricebook min) as strikethrough; fall back to priceMax.
  const rangeStrikePrice = (isRange && listPrice > priceMin) ? listPrice : priceMax;

  const showSalePriceStyles = hasDiscount || (isRange && priceMin < rangeStrikePrice);

  const currencyCode = hit.currency ?? currency ?? 'AUD';
  const priceData = {
    currentPrice: salePrice ?? priceMin ?? 0,
    listPrice: hasDiscount ? listPrice : null,
    isOnSale: hasDiscount,
    isASet: false,
    isMaster: false,
    isRange,
  };

  const productId = hit.productId || hit.id;
  const hasImage = Boolean(imageUrl);
  const handleSelect = () => onSelect(productId);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (onViewDetails) {
      onViewDetails(productId);
    }
  };

  return (
    <Box
      {...styles.tile}
      {...(isSelected ? styles.tileSelected : {})}
      aria-checked={isSelected}
      aria-label={title}
      className={[
        'choose-your-layout-tile',
        hasImage ? null : 'choose-your-layout-tile--no-image',
      ].filter(Boolean).join(' ')}
      {...sfccProductIdDataAttrs('product', productId)}
      role="radio"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      {/* Image — hidden when PIM thumbnail is missing (design: no placeholder). */}
      {hasImage ? (
        <Box className="choose-your-layout-tile-image-wrapper" {...styles.tileImageWrapper}>
          <Image
            alt={title}
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            src={imageUrl}
            {...styles.tileImage}
          />
        </Box>
      ) : null}

      {/* Text content */}
      <Box className="choose-your-layout-tile-content" {...styles.tileContent}>
        {badgeText && (
          <Text
            {...styles.tileBadge}
            {...(
              badgeText.toUpperCase().includes('SALE') ? styles.tileBadgeSale :
                badgeText.toUpperCase().includes('NEW') ? styles.tileBadgeNew :
                  badgeText.toUpperCase().includes('POPULAR') ? styles.tileBadgePopular : {}
            )}
          >
            {badgeText.toUpperCase().trim() === 'SALE' ? 'Sale' : badgeText}
          </Text>
        )}
        <Text className="product-tile__name" {...styles.tileTitle}>{title}</Text>
        {shortDescription && (
          <Markup source={shortDescription} {...styles.tileDescription} />
        )}
        {onViewDetails && (
          <Text
            {...styles.tileViewDetails}
            as="span"
            tabIndex={-1}
            onClick={handleViewDetails}
          >
            <FormattedMessage
              defaultMessage="View details"
              id="configuration_selector.tile.view_details"
            />
          </Text>
        )}
      </Box>

      {/* Price: range = "From MIN MAX" (max strikethrough); single = DisplayPrice */}
      {(priceMin != null || priceMax != null) && (
        <Box className="choose-your-layout-tile-price" {...styles.tilePrice}>
          <Box {...styles.tilePriceMin}>
            {isRange ? (
              <>
                <Text
                  as="span"
                  {...styles.tilePriceFrom}
                  {...(showSalePriceStyles ? styles.tilePriceFromSale : {})}
                >
                  <FormattedMessage
                    defaultMessage="From"
                    id="configuration_selector.tile.price_from"
                  />
                </Text>
                {' '}
                <Text
                  as="span"
                  aria-label={formatCurrencyFullPrecision(intl, priceMin, currencyCode)}
                  {...styles.tilePriceValue}
                  {...(showSalePriceStyles ? styles.tilePriceValueSale : {})}
                >
                  {formatCurrencyHideTrailingZeroCents(intl, priceMin, currencyCode)}
                </Text>
                {' '}
                <Text
                  as="span"
                  aria-label={formatCurrencyFullPrecision(intl, rangeStrikePrice, currencyCode)}
                  {...styles.tilePriceStrike}
                >
                  {formatCurrencyHideTrailingZeroCents(intl, rangeStrikePrice, currencyCode)}
                </Text>
              </>
            ) : (
              <>
                <Text
                  as="b"
                  aria-label={formatCurrencyFullPrecision(intl, priceData.currentPrice, currencyCode)}
                  {...styles.tilePriceValue}
                  {...(hasDiscount ? styles.tilePriceValueSale : {})}
                >
                  {formatCurrencyHideTrailingZeroCents(intl, priceData.currentPrice, currencyCode)}
                </Text>
                {hasDiscount && listPrice != null && (
                  <>
                    {' '}
                    <Text
                      as="span"
                      aria-label={formatCurrencyFullPrecision(intl, listPrice, currencyCode)}
                      {...styles.tilePriceStrike}
                    >
                      {formatCurrencyHideTrailingZeroCents(intl, listPrice, currencyCode)}
                    </Text>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Radio indicator */}
      <Box className="choose-your-layout-tile-radio" {...styles.tileRadio}>
        {isSelected && <Box className="choose-your-layout-tile-radio-dot" {...styles.tileRadioDot} />}
      </Box>
    </Box>
  );
};

SelectorTile.displayName = 'SelectorTile';

SelectorTile.propTypes = {
  hit: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
};

export default SelectorTile;
